import { diagnosticsRepo } from '../../data/repositories';
import { subscribe, type AppEvent } from '../events';

/**
 * Local-only diagnostics, **metadata only, never utterance text** (NFR7).
 *
 * `UtteranceSpoken` carries the text because `LearningService` needs it in Phase 2. This service
 * is the only other consumer, and it must not store it. The stripping therefore happens in exactly
 * one place — `toRecord` below — and is unit-tested directly, because a privacy guarantee spread
 * across call sites is a privacy guarantee that decays into a comment.
 *
 * What is captured is what substitutes for a clinical assessment we cannot obtain: activation
 * latency, mis-hit positions and offsets, which provider served each utterance, and audio losses.
 * The position data becomes the heatmap that tells a field loss from an acuity limitation (§5.5).
 */

export interface DiagnosticRecord {
  type: string;
  payload: Record<string, unknown>;
}

/**
 * Failure reasons are stored as a **classification, never as a message**.
 *
 * A provider's error string is not ours: it can carry an upstream error body, and an upstream body
 * could quote what was sent. Truncating free text to 120 characters is not a guarantee — the first
 * 120 characters of an utterance are still the utterance. Reducing to a fixed vocabulary makes a
 * leak structurally impossible instead of merely unlikely, and loses nothing that diagnostics
 * actually use: which provider failed, and what kind of failure it was.
 */
export function classifyReason(reason: string): string {
  const text = reason.toLowerCase();
  if (text.includes('abort')) return 'aborted';
  if (text.includes('unavailable')) return 'unavailable';
  if (text.includes('no cached audio')) return 'cache_miss';
  if (text.includes('text too long')) return 'text_too_long';
  if (text.includes('audio_context')) return 'audio_context';
  if (text.includes('timeout') || text.includes('timed out')) return 'timeout';
  const http = /tts_http_(\d{3})/.exec(text);
  if (http) return `http_${http[1]}`;
  if (text.includes('load failed') || text.includes('network')) return 'network';
  return 'other';
}

/**
 * Event → stored record. Pure, so the guarantee is testable.
 *
 * Returns `null` for events not worth storing. Note `UtteranceSpoken` keeps only the length and
 * the provider: length distinguishes a one-word utterance from a sentence for the WPM measure,
 * and cannot reconstruct content.
 */
export function toRecord(event: AppEvent): DiagnosticRecord | null {
  switch (event.type) {
    case 'UtteranceSpoken':
      return {
        type: 'UtteranceSpoken',
        payload: {
          source: event.source,
          providerId: event.providerId,
          chars: event.text.length,
          // A latency, and absent when nothing could measure it. Kept because a cold backend and a
          // dead AudioContext look identical from outside the device, and C80 records a defect
          // closed against the wrong one of them for want of this number.
          ...(event.msToAudio === undefined ? {} : { msToAudio: event.msToAudio }),
        },
      };
    case 'KeyActivated':
      return {
        type: 'KeyActivated',
        payload: { latencyMs: event.latencyMs, x: event.position.x, y: event.position.y },
      };
    case 'ActivationMissed':
      return {
        type: 'ActivationMissed',
        payload: {
          x: event.position.x,
          y: event.position.y,
          offsetX: event.offset.x,
          offsetY: event.offset.y,
        },
      };
    case 'PredictionAccepted':
      return { type: 'PredictionAccepted', payload: { slot: event.slot } };
    case 'LowEffortToggled':
      return { type: 'LowEffortToggled', payload: { on: event.on } };
    case 'ScanSelection':
      return { type: 'ScanSelection', payload: { steps: event.steps } };
    case 'DwellCancelled':
      // The reason, not the target. Which key was abandoned is as much a part of the utterance as which
      // key was pressed — the same reasoning that keeps `KeyActivated`'s key out of the record below.
      return { type: 'DwellCancelled', payload: { reason: event.reason } };
    case 'SpeechFailed':
      return {
        type: 'SpeechFailed',
        payload: { providerId: event.providerId, reason: classifyReason(event.reason) },
      };
    case 'AudioContextLost':
      return { type: 'AudioContextLost', payload: {} };
    case 'AudioRepairAttempted':
      // Whether it worked, and nothing else. The count per outcome is the reading (`DIA-05`).
      return { type: 'AudioRepairAttempted', payload: { ok: event.ok } };
    default:
      return null;
  }
}

/**
 * Which key was pressed is deliberately **not** recorded, even though `KeyActivated` carries it:
 * a sequence of keys is the utterance. Position is kept because the heatmap needs it and a
 * coordinate without the key is not text.
 */

let unsubscribe: (() => void) | null = null;

export function startDiagnostics(): () => void {
  if (unsubscribe) return unsubscribe;

  unsubscribe = subscribe((event) => {
    const record = toRecord(event);
    if (!record) return;
    // Fire and forget: diagnostics must never be on the critical path of speaking.
    void diagnosticsRepo.add(record.type, record.payload).catch((err: unknown) => {
      console.error('[diagnostics] could not persist', record.type, err);
    });
  });

  return unsubscribe;
}

export function stopDiagnostics(): void {
  unsubscribe?.();
  unsubscribe = null;
}
